/**
 * Junyang glass pointer light and one-shot page reveal.
 *
 * Ported from the skill's effects.js into the console bundle. No storage, no
 * network, no business state, and no animation loop while the pointer is still.
 * A second mount on the same root returns the existing instance so React
 * strict mode cannot stack listeners.
 */

const instances = new WeakMap<HTMLElement, JunyangGlassApi>()

export interface JunyangGlassApi {
  readonly reducedMotion: boolean
  setReducedMotion: (value: boolean) => void
  refresh: () => void
  destroy: () => void
}

function mediaOf(query: string): MediaQueryList {
  if (typeof window.matchMedia !== 'function') {
    return {
      matches: false,
      media: query,
      addEventListener: () => {},
      removeEventListener: () => {},
      addListener: () => {},
      removeListener: () => {},
      dispatchEvent: () => false,
      onchange: null,
    }
  }
  return window.matchMedia(query)
}

function tagSections(root: HTMLElement): void {
  const hosts = root.querySelectorAll<HTMLElement>('.u-page, [data-jg-page]')
  hosts.forEach((host) => {
    let nodes = [...host.children].filter((node): node is HTMLElement => node instanceof HTMLElement)
    const only = nodes.length === 1 ? nodes[0] : undefined
    if (only) {
      const inner = [...only.children].filter((node): node is HTMLElement => node instanceof HTMLElement)
      if (inner.length >= 2) nodes = inner
    }
    nodes.forEach((node, index) => {
      if (node.dataset.jgReveal) return
      const heavy = node.matches('table') || node.querySelector('table, svg') !== null
      node.dataset.jgReveal = heavy ? 'plain' : 'soft'
      node.dataset.jgRevealIndex = String(index)
    })
  })
}

export function mountJunyangGlass(root: HTMLElement): JunyangGlassApi {
  const existing = instances.get(root)
  if (existing) return existing
  if (!root.hasAttribute('data-jg')) {
    throw new TypeError('Expected a [data-jg] container')
  }

  const system = mediaOf('(prefers-reduced-motion: reduce)')
  const precise = mediaOf('(hover: hover) and (pointer: fine)')
  const forced = mediaOf('(forced-colors: active)')
  const seen = new WeakSet<Element>()
  const running = new Set<Animation>()
  const cleanups: Array<() => void> = []
  let disposed = false
  let frame = 0
  let pending: { target: HTMLElement; x?: number; y?: number; center?: boolean } | null = null
  let lit: HTMLElement | null = null
  let reduced = false

  const listens = (
    target: EventTarget,
    type: string,
    fn: EventListener,
    options?: AddEventListenerOptions,
  ): void => {
    target.addEventListener(type, fn, options)
    cleanups.push(() => {
      target.removeEventListener(type, fn, options)
    })
  }

  const eligible = (target: HTMLElement | null): target is HTMLElement =>
    Boolean(
      target &&
        root.contains(target) &&
        target.closest('[data-jg]') === root &&
        target.isConnected &&
        !target.closest('[inert], :disabled, [aria-disabled="true"], [aria-busy="true"]'),
    )

  const clearLight = (): void => {
    if (lit) {
      delete lit.dataset.jgLit
      lit.style.removeProperty('--jg-light-x')
      lit.style.removeProperty('--jg-light-y')
    }
    lit = null
  }

  const cancelFrame = (): void => {
    if (frame) window.cancelAnimationFrame(frame)
    frame = 0
    pending = null
  }

  const paintLight = (): void => {
    frame = 0
    const item = pending
    pending = null
    if (disposed || reduced || forced.matches || document.hidden || !item || !eligible(item.target)) {
      clearLight()
      return
    }
    const rect = item.target.getBoundingClientRect()
    if (!rect.width || !rect.height) {
      clearLight()
      return
    }
    if (lit !== item.target) clearLight()
    lit = item.target
    const x = item.center ? rect.width / 2 : Math.min(rect.width, Math.max(0, (item.x ?? 0) - rect.left))
    const y = item.center ? rect.height / 2 : Math.min(rect.height, Math.max(0, (item.y ?? 0) - rect.top))
    lit.style.setProperty('--jg-light-x', `${x}px`)
    lit.style.setProperty('--jg-light-y', `${y}px`)
    lit.dataset.jgLit = 'true'
  }

  const queueLight = (item: { target: HTMLElement; x?: number; y?: number; center?: boolean }): void => {
    pending = item
    if (!frame) frame = window.requestAnimationFrame(paintLight)
  }

  const nearest = (event: Event): HTMLElement | null => {
    const target = event.target
    if (!(target instanceof Element)) return null
    const found = target.closest('[data-jg-light]')
    return found instanceof HTMLElement ? found : null
  }

  listens(
    root,
    'pointermove',
    (event) => {
      if (!(event instanceof PointerEvent)) return
      if (!precise.matches || event.pointerType === 'touch' || reduced || forced.matches || document.hidden) return
      const target = nearest(event)
      if (!eligible(target)) {
        cancelFrame()
        clearLight()
        return
      }
      queueLight({ target, x: event.clientX, y: event.clientY })
    },
    { passive: true },
  )

  listens(
    root,
    'pointerout',
    (event) => {
      const target = nearest(event)
      if (target && event instanceof PointerEvent && event.relatedTarget instanceof Node && target.contains(event.relatedTarget)) {
        return
      }
      cancelFrame()
      clearLight()
    },
    { passive: true },
  )

  listens(root, 'focusin', (event) => {
    const target = nearest(event)
    if (!reduced && eligible(target) && target.matches(':focus-visible')) queueLight({ target, center: true })
  })

  listens(root, 'focusout', () => {
    cancelFrame()
    clearLight()
  })

  const stopAnimations = (): void => {
    running.forEach((animation) => {
      animation.cancel()
    })
    running.clear()
  }

  const applyMotion = (): void => {
    reduced = system.matches || root.dataset.jgMotion === 'reduced'
    if (reduced) {
      cancelFrame()
      clearLight()
      stopAnimations()
    }
  }

  listens(system, 'change', applyMotion)
  listens(precise, 'change', () => {
    cancelFrame()
    clearLight()
  })
  listens(forced, 'change', () => {
    cancelFrame()
    clearLight()
  })
  applyMotion()

  const duration = (): number => {
    const value = window.getComputedStyle(root).getPropertyValue('--jg-reveal-time').trim()
    const parsed = Number.parseFloat(value)
    if (!Number.isFinite(parsed)) return 620
    return value.endsWith('ms') ? parsed : parsed * 1000
  }

  const intersection =
    typeof IntersectionObserver === 'function'
      ? new IntersectionObserver(
          (entries) => {
            const time = duration()
            entries.forEach(({ target, isIntersecting }) => {
              if (!isIntersecting || !(target instanceof HTMLElement)) return
              intersection?.unobserve(target)
              if (disposed || reduced || typeof target.animate !== 'function') return
              const start: Keyframe = { opacity: 0.3, transform: 'translateY(12px)' }
              const end: Keyframe = { opacity: 1, transform: 'translateY(0)' }
              if (target.dataset.jgReveal === 'soft') {
                start.filter = 'blur(5px)'
                end.filter = 'blur(0px)'
              }
              const index = Number(target.dataset.jgRevealIndex || 0)
              try {
                const animation = target.animate([start, end], {
                  duration: time,
                  delay: Math.max(0, Math.min(Number.isFinite(index) ? index : 0, 4)) * 55,
                  easing: 'cubic-bezier(.2,.7,.2,1)',
                })
                running.add(animation)
                animation.finished.then(
                  () => {
                    running.delete(animation)
                  },
                  () => {
                    running.delete(animation)
                  },
                )
                if (document.hidden) animation.pause()
              } catch {
                // The section stays as authored if the animation cannot start.
              }
            })
          },
          { threshold: 0.05 },
        )
      : null

  const register = (element: Element): void => {
    if (element.closest('[data-jg]') !== root || seen.has(element)) return
    seen.add(element)
    if (!reduced && intersection) intersection.observe(element)
  }

  const refresh = (): void => {
    if (disposed) return
    tagSections(root)
    root.querySelectorAll('[data-jg-reveal]').forEach(register)
  }

  const mutations =
    typeof MutationObserver === 'function'
      ? new MutationObserver((records) => {
          if (records.some((record) => record.type === 'attributes')) {
            applyMotion()
            if (lit && !eligible(lit)) {
              cancelFrame()
              clearLight()
            }
          }
          records.forEach((record) => {
            record.addedNodes.forEach((node) => {
              if (!(node instanceof Element)) return
              if (node.matches('[data-jg-reveal]')) register(node)
              node.querySelectorAll('[data-jg-reveal]').forEach(register)
            })
          })
          if (records.some((record) => record.type === 'childList')) refresh()
        })
      : null

  if (mutations) {
    mutations.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-jg-motion', 'disabled', 'aria-disabled', 'aria-busy', 'inert'],
    })
  }

  listens(document, 'visibilitychange', () => {
    if (document.hidden) {
      cancelFrame()
      clearLight()
    }
    running.forEach((animation) => {
      if (animation.playState === 'finished' || animation.playState === 'idle') return
      if (document.hidden) animation.pause()
      else if (!reduced) animation.play()
    })
  })

  refresh()

  const api: JunyangGlassApi = {
    get reducedMotion() {
      return reduced
    },
    setReducedMotion(value: boolean) {
      root.dataset.jgMotion = value ? 'reduced' : 'standard'
      applyMotion()
    },
    refresh,
    destroy() {
      if (disposed) return
      disposed = true
      cancelFrame()
      clearLight()
      stopAnimations()
      intersection?.disconnect()
      mutations?.disconnect()
      cleanups.forEach((cleanup) => {
        cleanup()
      })
      instances.delete(root)
    },
  }
  instances.set(root, api)
  return api
}
